import Vue from "vue";
import Plan from "./index.vue";
import {
  createApplicationPlan,
  createApplicationCompetitorEmbedded,
  getPlanSettings,
  getTimeboxElement,
  getTimelineSizes,
  createApplicationStudentEmbedded
} from "@/helpers/plan-helper";
// import tempJson from "./temp/company.js";
// import tempPlan from "@/api/mocks/plan.ts";
// import {
//   competitorEmbedded1,
//   competitorEmbedded2,
//   competitorEmbedded3,
//   competitorEmbedded4,
//   competitorEmbedded5
// } from "@/api/mocks/competitorEmbedded.ts";
import {
  ContainerData,
  ContainerMethods,
  ContainerComputed,
  ContainerProps
} from "./types";
import { ApiResponse, NotFoundError } from "@/api/types";

const SIMULTANEOUS_FETCH_COUNT = 5;

const DEFAULT_STUDENT_TREND_CATEGORY = "MEMBER_REGISTRATION_COUNT";

export default Vue.extend<
  ContainerData,
  ContainerMethods,
  ContainerComputed,
  ContainerProps
>({
  data() {
    return {
      fetchingCompetitorIds: [],
      fetchingStudentIds: [],
      isDoneMounted: false
    };
  },

  computed: {
    planSettings() {
      return this.$store.state.pages.plan.planSettings;
    },
    selectedGraduateYears() {
      return this.$store.state.pages.plan.selectedGraduateYears;
    },
    applicationPlan() {
      return this.$store.state.pages.plan.applicationPlan;
    },
    selectedEvent() {
      return this.$store.state.pages.plan.selectedEvent;
    },
    selectedRecruitmentEvent() {
      return this.$store.getters["pages/plan/getSelectedRecruitmentEvent"];
    },
    selectedCompetitorEvent() {
      return this.$store.getters["pages/plan/getSelectedCompetitorEvent"];
    },
    selectedStudentEvent() {
      return this.$store.getters["pages/plan/getSelectedStudentEvent"];
    },
    selectedUniversityId() {
      return this.$store.state.pages.plan.selectedUniversityId;
    },
    selectedStudentTrendCategory() {
      return this.$store.state.pages.plan.selectedStudentTrendCategory;
    },
    competitorEmbeddeds() {
      // NOTE: モックデータ確認用
      // return [
      //   competitorEmbedded1,
      //   competitorEmbedded2,
      //   competitorEmbedded3,
      //   competitorEmbedded4,
      //   competitorEmbedded5
      // ];
      return this.$store.state.domains.plan.competitorEmbeddeds;
    },
    studentEmbeddeds() {
      return this.$store.state.domains.plan.studentEmbeddeds;
    },
    isUpdateUniversity() {
      return this.$store.state.domains.plan.isUpdateUniversity;
    },
    competitorErrors() {
      return this.$store.state.domains.plan.competitorErrors;
    },
    applicationCompetitorEmbeddeds() {
      return this.$store.state.pages.plan.applicationCompetitorEmbeddeds;
    },
    applicationStudentEmbeddeds() {
      return this.$store.state.pages.plan.applicationStudentEmbeddeds;
    },
    isLoading() {
      return this.$store.state.pages.plan.isLoading;
    },
    currentPlan() {
      // NOTE: モックデータ確認用
      // return tempPlan;
      return this.$store.state.domains.plan.currentPlan;
    },
    partnerUser() {
      return this.$store.state.domains.user.partnerUser;
    },
    companies() {
      return this.$store.state.pages.plan.companies;
    },
    planId() {
      return Number(this.$route.params["planId"]);
    },
    graduate_year() {
      return this.applicationPlan && this.applicationPlan.graduate_year;
    },
    distinctUniversities() {
      if (!this.applicationPlan) {
        return [];
      }
      const pushedUniversityIds: number[] = [];
      const distinctUniversities = this.applicationPlan.student_segments
        .map(e => e.university)
        .filter(e => {
          const id = e.id;
          if (!pushedUniversityIds.includes(id)) {
            pushedUniversityIds.push(id);
            return true;
          }
          return false;
        });
      return distinctUniversities;
    },
    studentJobHuntingTrendCollection() {
      return this.$store.state.domains.plan.studentJobHuntingTrendCollection;
    }
  },
  watch: {
    planId() {
      this.fetchPlan();
    },
    currentPlan() {
      this.updateApplicationPlan();
    },
    applicationPlan() {
      const newCompetitorIds = this.getNewCompetitorsIds();
      const newStudentIds = this.getNewStudentsIds();
      if (newCompetitorIds.length) {
        this.fetchCompetitorEmbeddedRecursively(newCompetitorIds);
      }
      if (newStudentIds.length) {
        this.fetchStudentEmbeddedRecursively(newStudentIds);
      }
    },
    competitorEmbeddeds() {
      this.updateApplicationCompetitorEmbeddeds(true);
    },
    studentEmbeddeds() {
      const useCache = !this.isUpdateUniversity;
      this.updateApplicationStudentEmbeddeds(useCache);
      if (this.isUpdateUniversity) {
        this.$store.commit("domains/plan/resetIsUpdateUniversity");
      }
    },
    "planSettings.timeLineMagnification"() {
      Vue.nextTick(this.handleResize);
    },
    distinctUniversities: {
      immediate: true,
      deep: true,
      handler(
        val: ApiResponse.University[],
        old: ApiResponse.University[] = []
      ) {
        // 初回描画時のみ実行(DBの選択大学を優先する)
        if (!this.isDoneMounted) {
          const universityIdFromDistinctUniversities = val[0] && val[0].id;
          const defaultUniversityId =
            this.applicationPlan &&
            this.applicationPlan.default_job_trend_university_id
              ? this.applicationPlan.default_job_trend_university_id
              : universityIdFromDistinctUniversities;
          if (
            defaultUniversityId &&
            this.distinctUniversities.some(e => e.id === defaultUniversityId)
          ) {
            this.handleUpdateSelectedUniversityId(defaultUniversityId);
            return;
          }
        }
        if (val.length === 0) {
          this.handleUpdateSelectedUniversityId(undefined);
          this.handleUpdateSelectedStudentTrendCategory(
            DEFAULT_STUDENT_TREND_CATEGORY
          );
        } else if (val.length >= 1) {
          const registeredUniversityIds = val.map(e => e.id);

          // NOTE 大学編集で選択中の大学名を変更した場合
          if (
            val.length === old.length &&
            this.selectedUniversityId &&
            !val.some(e => e.id === this.selectedUniversityId)
          ) {
            // NOTE 何番目の大学を編集したか
            const editedUniversityIndex = old.findIndex(
              e => e.id === this.selectedUniversityId
            );
            // NOTE 編集後の大学IDに更新する
            this.handleUpdateSelectedUniversityId(
              val[editedUniversityIndex].id
            );

            // NOTE 選択中大学IDがundefined or 登録大学ID一覧に選択中の大学IDが含まれていない場合は一番上に表示されている大学のIDで更新する
          } else if (
            !this.selectedUniversityId ||
            (this.selectedUniversityId &&
              !registeredUniversityIds.includes(this.selectedUniversityId))
          ) {
            this.handleUpdateSelectedUniversityId(val[0].id);
          }
          this.handleUpdateSelectedStudentTrendCategory(
            DEFAULT_STUDENT_TREND_CATEGORY
          );
        }
      }
    },
    selectedUniversityId: {
      immediate: true,
      handler(val) {
        if (!val) return;
        this.fetchStudentJobHuntingTrendCollection();
      }
    }
  },
  async mounted() {
    this.initializeLocalPlanSettings();
    await this.fetchPlan();
    await this.initSelectedUniversityId();
    this.updateApplicationCompetitorEmbeddeds(false);
    this.updateApplicationStudentEmbeddeds(false);
    window.addEventListener("resize", this.handleResize);
    this.isDoneMounted = true;
    this.initializeLocalGraduateYears();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    async fetchPlan() {
      try {
        if (!this.partnerUser) {
          return;
        }
        this.$store.commit("pages/plan/updateLoading", {
          isLoading: true
        });
        await this.$store.dispatch("domains/plan/getPlan", {
          partner_id: this.partnerUser.partner.id,
          plan_id: this.planId
        });
      } catch (error) {
        if (error instanceof NotFoundError) {
          this.$router.push({ name: "Plans" }).catch(err => err);
        }

        throw error;
      } finally {
        this.$store.commit("pages/plan/updateLoading", {
          isLoading: false
        });
      }
    },
    async fetchCompetitorEmbedded(competitorId, planId) {
      try {
        if (!this.partnerUser || !this.applicationPlan) {
          return;
        }
        const competitor = this.applicationPlan.competitors.find(competitor => {
          return competitor.id === competitorId;
        });
        if (!competitor) {
          return;
        }
        await this.$store.dispatch("domains/plan/getCompetitorEmbedded", {
          partnerId: this.partnerUser.partner.id,
          planId: planId || this.planId,
          competitorId,
          companyId: competitor.company.id
        });
      } catch (error) {
        throw error;
      }
    },
    async fetchStudentJobHuntingTrendCollection() {
      try {
        if (
          !this.partnerUser ||
          !this.applicationPlan ||
          !this.applicationPlan.student_segments.length
        ) {
          return;
        }
        // NOTE ストアに選択大学IDが存在しない場合、登録済み大学からグラフ情報を取得する
        const targetUniversityId =
          this.selectedUniversityId ||
          this.applicationPlan.student_segments[0].university.id;
        if (!targetUniversityId) {
          this.$store.commit(
            "domains/plan/resetStudentJobHuntingTrendCollection"
          );
          return;
        }
        await this.$store.dispatch(
          "domains/plan/getStudentJobHuntingTrendCollection",
          {
            partnerId: this.partnerUser.partner.id,
            planId: this.planId,
            universityId: targetUniversityId
          }
        );
      } catch (error) {
        throw error;
      }
    },
    fetchCompetitorEmbeddedRecursively(competitorIds) {
      // NOTE: タスクが残っている状態でプランが変わると404になるため、local変数に代入
      const planId = this.planId;

      // NOTE: 同企業データの重複リクエストを避けるためにリクエスト中の他社IDを保持しておく
      competitorIds.forEach(competitorId => {
        if (!this.fetchingCompetitorIds.includes(competitorId)) {
          this.fetchingCompetitorIds.push(competitorId);
        }
      });
      const fetchCompetitors = (fetchCompetitorIds: number[]) => {
        if (!fetchCompetitorIds.length) {
          return;
        }
        Promise.all(
          fetchCompetitorIds.map(competitorId =>
            this.fetchCompetitorEmbedded(competitorId, planId)
          )
        ).then(() => {
          // NOTE: リクエスト完了後は保持していた他社IDを解放する
          this.fetchingCompetitorIds = this.fetchingCompetitorIds.filter(
            competitorId => {
              return !fetchCompetitorIds.includes(competitorId);
            }
          );
          fetchCompetitors(competitorIds.splice(0, SIMULTANEOUS_FETCH_COUNT));
        });
      };
      fetchCompetitors(competitorIds.splice(0, SIMULTANEOUS_FETCH_COUNT));
    },
    getNewCompetitorsIds() {
      if (!this.applicationPlan) {
        return [];
      }
      const planCompetitorIds = this.applicationPlan.competitors.map(
        competitor => {
          return competitor.id;
        }
      );
      const competitorEmbeddedIds = this.competitorEmbeddeds.map(competitor => {
        return competitor.id;
      });
      const newCompetitorIds = planCompetitorIds.filter(competitorId => {
        return (
          competitorId > -1 &&
          // NOTE: 既に取得済みの他社を除く
          !competitorEmbeddedIds.includes(competitorId) &&
          // NOTE: 現在リクエスト中の他社を除く
          !this.fetchingCompetitorIds.includes(competitorId)
        );
      });
      return newCompetitorIds;
    },
    async fetchStudentEmbedded(studentId, planId) {
      try {
        if (!this.partnerUser || !this.applicationPlan) {
          return;
        }
        const student = this.applicationPlan.student_segments.find(student => {
          return student.id === studentId;
        });
        if (!student) {
          return;
        }
        await this.$store.dispatch("domains/plan/getStudentEmbedded", {
          partnerId: this.partnerUser.partner.id,
          planId: planId || this.planId,
          studentId
        });
      } catch (error) {
        throw error;
      }
    },
    fetchStudentEmbeddedRecursively(studentIds) {
      // NOTE: タスクが残っている状態でプランが変わると404になるため、local変数に代入
      const planId = this.planId;

      // NOTE: 学生データの重複リクエストを避けるためにリクエスト中の学生IDを保持しておく
      studentIds.forEach(studentId => {
        if (!this.fetchingStudentIds.includes(studentId)) {
          this.fetchingStudentIds.push(studentId);
        }
      });
      const fetchStudents = (fetchStudentIds: number[]) => {
        if (!fetchStudentIds.length) {
          return;
        }
        Promise.all(
          fetchStudentIds.map(studentId =>
            this.fetchStudentEmbedded(studentId, planId)
          )
        ).then(() => {
          // NOTE: リクエスト完了後は保持していた学生IDを解放する
          this.fetchingStudentIds = this.fetchingStudentIds.filter(
            studentId => {
              return !fetchStudentIds.includes(studentId);
            }
          );
          fetchStudents(studentIds.splice(0, SIMULTANEOUS_FETCH_COUNT));
        });
      };
      fetchStudents(studentIds.splice(0, SIMULTANEOUS_FETCH_COUNT));
    },
    getNewStudentsIds() {
      if (!this.applicationPlan) {
        return [];
      }
      const planStudentIds = this.applicationPlan.student_segments.map(
        student => {
          return student.id;
        }
      );
      const studentEmbeddedIds = this.studentEmbeddeds.map(student => {
        return student.id;
      });
      const newStudentIds = planStudentIds.filter(studentId => {
        return (
          studentId > -1 &&
          // NOTE: 既に取得済みの学生を除く
          !studentEmbeddedIds.includes(studentId) &&
          // NOTE: 現在リクエスト中の学生を除く
          !this.fetchingStudentIds.includes(studentId)
        );
      });
      return newStudentIds;
    },
    updateApplicationPlan() {
      if (!this.currentPlan) {
        return;
      }
      this.$store.commit("pages/plan/updatePlan", {
        plan: createApplicationPlan(
          this.currentPlan,
          getTimelineSizes(getTimeboxElement()).part.width
        )
      });
      this.initializeLocalPlanSettings();
      this.initializeLocalGraduateYears();
    },
    updateApplicationCompetitorEmbeddeds(useCache) {
      this.competitorEmbeddeds.forEach(competitorEmbedded => {
        const hasCache =
          useCache &&
          this.applicationCompetitorEmbeddeds.some(
            applicationCompetitorEmbedded => {
              return applicationCompetitorEmbedded.id === competitorEmbedded.id;
            }
          );
        if (hasCache) {
          return;
        }
        const applicationCompetitorEmbedded = createApplicationCompetitorEmbedded(
          competitorEmbedded,
          getTimelineSizes(getTimeboxElement()).part.width
        );
        this.$store.commit("pages/plan/updateCompetitorEmbedded", {
          competitorEmbedded: applicationCompetitorEmbedded
        });
      });
    },
    updateApplicationStudentEmbeddeds(useCache) {
      this.studentEmbeddeds.forEach(studentEmbedded => {
        const hasCache =
          useCache &&
          this.applicationStudentEmbeddeds.some(applicationStudentEmbedded => {
            return applicationStudentEmbedded.id === studentEmbedded.id;
          });
        if (hasCache) {
          return;
        }
        const applicationStudentEmbedded = createApplicationStudentEmbedded(
          studentEmbedded,
          this.graduate_year!,
          getTimelineSizes(getTimeboxElement()).part.width
        );

        this.$store.commit("pages/plan/updateStudentEmbedded", {
          studentEmbedded: applicationStudentEmbedded
        });
      });
    },
    handleResize() {
      this.updateApplicationPlan();
      this.updateApplicationCompetitorEmbeddeds(false);
      this.updateApplicationStudentEmbeddeds(false);
    },
    handleOwnOrganizationEventClick(eventId) {
      if (!this.applicationPlan) {
        return;
      }
      if (this.selectedEvent && this.selectedEvent.id === eventId) {
        this.initSelectedEvent();
        return;
      }
      const clickedEvent = this.applicationPlan.recruitment_events_with_position.find(
        event => {
          return event.id === eventId;
        }
      );

      this.$store.commit("pages/plan/updateSelectedEvent", {
        event: clickedEvent
      });
    },
    initSelectedEvent() {
      this.$store.commit("pages/plan/updateSelectedEvent", {
        event: undefined
      });
    },
    initSelectedUniversityId() {
      if (
        this.applicationPlan &&
        this.distinctUniversities.length &&
        !this.selectedUniversityId
      ) {
        let universityId = undefined;
        // NOTE DBに選択大学IDが保存されている場合はそちらを採用
        if (this.applicationPlan.default_job_trend_university_id) {
          universityId = this.applicationPlan.default_job_trend_university_id;
        } else {
          universityId = this.distinctUniversities[0].id;
        }
        this.$store.commit("pages/plan/updateSelectedUniversityId", {
          universityId
        });
      }
    },
    initSelectedStudentTrendCategory() {
      this.$store.commit("pages/plan/updateSelectedStudentTrendCategory", {
        studentTrendCategory: DEFAULT_STUDENT_TREND_CATEGORY
      });
    },
    handleOuterEventClick() {
      this.initSelectedEvent();
    },
    handleCreatedOwnOrganizationEvent() {
      this.fetchPlan();
    },
    handleDeletedOwnOrganizationEvent() {
      this.fetchPlan();
      this.initSelectedEvent();
    },
    handleCompetitorFloatingView() {
      this.$store.commit("pages/plan/updateCompetitorView", {
        isVisible: true
      });
    },
    handleUniversityFloatingView() {
      this.$store.commit("pages/plan/updateUniversityView", {
        isVisible: true
      });
    },
    handleCompetitorEventClick(payload) {
      if (!this.applicationCompetitorEmbeddeds) {
        return;
      }
      if (this.selectedEvent && this.selectedEvent.id === payload.event.id) {
        this.initSelectedEvent();
        return;
      }
      this.$store.commit("pages/plan/updateSelectedEvent", {
        event: payload.event
      });
      this.$store.commit("pages/plan/updateSelectedCompany", {
        company: payload.competitor.company
      });
    },
    handleStudentEventClick(payload) {
      if (!this.applicationStudentEmbeddeds) {
        return;
      }
      if (this.selectedEvent && this.selectedEvent.id === payload.event.id) {
        this.initSelectedEvent();
        return;
      }
      this.$store.commit("pages/plan/updateSelectedEvent", {
        event: payload.event
      });
      this.$store.commit("pages/plan/updateSelectedStudent", {
        student: payload.student
      });
    },
    handleCompetitorDelete(partnerId, planId, competitorId) {
      this.$store
        .dispatch("domains/plan/deleteCompetitor", {
          partnerId,
          planId,
          competitorId
        })
        .then(() => {
          this.$store.commit("pages/plan/deleteCompetitorEmbedded", {
            competitorId
          });
          this.fetchPlan();
        });
    },
    handleUniversityDelete(partnerId, planId, studentId) {
      this.$store
        .dispatch("domains/plan/deleteUniversity", {
          partnerId,
          planId,
          studentId
        })
        .then(() => {
          this.$store.commit("pages/plan/deleteUniversityEmbedded", {
            studentId
          });
          this.fetchPlan();
        });
    },
    handleUpdateSpeechBubble(position) {
      const vertexDirection = position.top === "auto" ? "bottom" : "top";
      this.$store.commit("pages/plan/updateSpeechBubble", {
        speechBubble: {
          position,
          vertexDirection
        }
      });
    },
    handleUpdateSelectedUniversityId(universityId) {
      this.$store.commit("pages/plan/updateSelectedUniversityId", {
        universityId
      });
    },
    handleUpdateSelectedStudentTrendCategory(studentTrendCategory) {
      this.$store.commit("pages/plan/updateSelectedStudentTrendCategory", {
        studentTrendCategory
      });
    },
    handleUpdateSelectedGraduateYears(graduateYear) {
      this.$store.commit("pages/plan/updateSelectedGraduateYears", {
        graduateYear: graduateYear,
        planId: this.planId
      });
    },
    handleUpdateCompetitorsOrder(eventIndexes) {
      if (!this.applicationPlan || !this.partnerUser) return;

      // NOTE: competitorsは既に並び替えられているのでoldIndexではなくnewIndexになる。
      const competitorId = this.applicationPlan.competitors[
        eventIndexes.newIndex
      ].id;

      this.$store.dispatch("pages/plan/updateCompetitorsOrder", {
        partnerId: this.partnerUser.partner.id,
        planId: this.planId,
        competitorId,
        company: { sort_order_position: eventIndexes.newIndex },
        oldIndex: eventIndexes.oldIndex
      });
    },
    handleUpdateUniversitiesOrder(eventIndexes) {
      if (!this.applicationPlan || !this.partnerUser) return;

      // NOTE: competitorsは既に並び替えられているのでoldIndexではなくnewIndexになる。
      const student = this.applicationPlan.student_segments[
        eventIndexes.newIndex
      ];
      const university = student.university;

      this.$store.dispatch("pages/plan/updateUniversitiesOrder", {
        partnerId: this.partnerUser.partner.id,
        planId: this.planId,
        studentId: student.id,
        university: {
          name: university.name,
          course: university.course,
          sort_order_position: eventIndexes.newIndex
        },
        oldIndex: eventIndexes.oldIndex
      });
    },
    courseName(course) {
      switch (course) {
        case "HUMANITIES":
          return "文";
        case "SCIENCE":
          return "理";
        case "UNSPECIFIED":
          return "未";
        default:
          return "";
      }
    },
    initializeLocalPlanSettings() {
      const localPlanSettings = getPlanSettings(this.planId);
      this.$store.commit("pages/plan/updatePlanSettings", {
        ...localPlanSettings,
        planId: this.planId
      });
    },
    initializeLocalGraduateYears() {
      this.$store.commit("pages/plan/initializeSelectedGraduateYears", {
        graduateYear: this.graduate_year!,
        planId: this.planId
      });
    },
    handleClickOwnEventToggle() {
      if (!this.currentPlan) return;
      this.$store.commit("pages/plan/updatePlanSettings", {
        planId: this.currentPlan.id,
        isShowOwnEventTimeline: !this.planSettings.isShowOwnEventTimeline
      });
    },
    handleClickCompetitorEventToggle() {
      if (!this.currentPlan) return;
      this.$store.commit("pages/plan/updatePlanSettings", {
        planId: this.currentPlan.id,
        isShowCompetitorEventTimeline: !this.planSettings
          .isShowCompetitorEventTimeline
      });
    },
    handleClickUniversityEventToggle() {
      if (!this.currentPlan) return;
      this.$store.commit("pages/plan/updatePlanSettings", {
        planId: this.currentPlan.id,
        isShowUniversityEventTimeline: !this.planSettings
          .isShowUniversityEventTimeline
      });
    },
    handleClickStudentTrendToggle() {
      if (!this.currentPlan) return;
      this.$store.commit("pages/plan/updatePlanSettings", {
        planId: this.currentPlan.id,
        isShowStudentTrendTimeline: !this.planSettings
          .isShowStudentTrendTimeline
      });
    }
  },
  render(createElement) {
    return createElement(Plan, {
      props: {
        partnerId: this.partnerUser ? this.partnerUser.partner.id : undefined,
        company: this.partnerUser
          ? this.partnerUser.partner.company
          : undefined,
        plan: this.applicationPlan,
        competitorEmbeddeds: this.applicationCompetitorEmbeddeds,
        studentEmbeddeds: this.applicationStudentEmbeddeds,
        competitorErrors: this.competitorErrors,
        selectedRecruitmentEvent: this.selectedRecruitmentEvent,
        selectedCompetitorEvent: this.selectedCompetitorEvent,
        selectedStudentEvent: this.selectedStudentEvent,
        selectedUniversityId: this.selectedUniversityId,
        selectedStudentTrendCategory: this.selectedStudentTrendCategory,
        companies: this.companies,
        isLoading: this.isLoading,
        distinctUniversities: this.distinctUniversities,
        studentJobHuntingTrendCollection: this.studentJobHuntingTrendCollection,
        planSettings: this.planSettings,
        selectedGraduateYears: this.selectedGraduateYears,
        existCredit: this.partnerUser && this.partnerUser.partner.exist_credit
      },
      on: {
        clickOwnOrganizationEvent: this.handleOwnOrganizationEventClick,
        clickCompetitorEvent: this.handleCompetitorEventClick,
        clickStudentEvent: this.handleStudentEventClick,
        clickOuterEvent: this.handleOuterEventClick,
        createdEvent: this.handleCreatedOwnOrganizationEvent,
        deletedEvent: this.handleDeletedOwnOrganizationEvent,
        competitorFloatingView: this.handleCompetitorFloatingView,
        universityFloatingView: this.handleUniversityFloatingView,
        competitorDeleteEvent: this.handleCompetitorDelete,
        universityDeleteEvent: this.handleUniversityDelete,
        updateSpeechBubble: this.handleUpdateSpeechBubble,
        clickOwnEventToggle: this.handleClickOwnEventToggle,
        clickCompetitorEventToggle: this.handleClickCompetitorEventToggle,
        clickUniversityEventToggle: this.handleClickUniversityEventToggle,
        clickStudentTrendToggle: this.handleClickStudentTrendToggle,
        updateSelectedUniversityId: this.handleUpdateSelectedUniversityId,
        updateSelectedStudentTrendCategory: this
          .handleUpdateSelectedStudentTrendCategory,
        updateSelectedGraduateYears: this.handleUpdateSelectedGraduateYears,
        updateCompetitorsOrder: this.handleUpdateCompetitorsOrder,
        updateUniversitiesOrder: this.handleUpdateUniversitiesOrder
      }
    });
  }
});
